import { AxiosRequestConfig } from 'axios';
import { callApi } from './api';
import User from '../models/user';
import Order from '../models/order';
import { useQuery } from '@tanstack/react-query';

const usersApiUrl = import.meta.env.VITE_API_URL_V1 + '/admin/users';

export const getUserProfile = async (userId: string, accessToken?: string) => {
    const config: AxiosRequestConfig = {
        url: `${usersApiUrl}/${userId}`,
        method: 'GET',
    };

    if (accessToken) {
        config.headers = {
            ...config.headers,
            Authorization: `Bearer ${accessToken}`,
        };
    }

    return callApi<UserDetail>(config);
};

/**
 * Server-side filters for the Users list. Every one of these is a Firestore
 * query constraint on the API side, not a client-side pass over a page of
 * results — spec v2 §03 requires them to work over the full user set.
 */
export interface UserFilters {
    search?: string;
    signupSource?: string;
    cohort?: string;
    /**
     * The STORED order status (`paid` | `fulfilling` | …), never the relabelled
     * display value. The UI shows Pending/Running/Shipped, but the customer
     * order tracker and the marketing site read the stored strings, so those are
     * what travel on the wire.
     */
    shipmentStatus?: string;
    /** Inclusive; the API widens a date-only upper bound to end of day. */
    createdFrom?: string;
    createdTo?: string;
}

/**
 * Everything the profile page needs, in ONE request (spec v2 §05 #2).
 *
 * `orders` and `telegram` are best-effort on the server: if either lookup
 * fails the user still renders, with an empty list rather than a 500.
 */
export interface UserDetail {
    user: User;
    orders: Order[];
    telegram: {
        joined?: boolean;
        joinedAt?: string;
        inviteSentAt?: string;
        inviteStatus?: string;
        inviteExpiresAt?: string;
        username?: string;
    };
    /** Non-blocking notes for a human, e.g. more than one live board order. */
    warnings: string[];
}

export const getUsers = async (page = 1, limit = 50, filters: UserFilters = {}) => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    // Empty strings must NOT be sent: the API treats a present-but-empty filter
    // as a real equality constraint and would return nobody.
    for (const [key, value] of Object.entries(filters)) {
        if (value) params.set(key, value);
    }
    return callApi<{ users: User[]; total: number; page: number; limit: number }>({
        url: `${usersApiUrl}?${params.toString()}`,
        method: 'GET',
    });
};

export const useUsers = (page = 1, limit = 10, filters: UserFilters = {}) => {
    return useQuery({
        // Filters are part of the key, so changing one refetches rather than
        // showing the previous filter's rows under the new label.
        queryKey: ['users', page, limit, filters],
        queryFn: () => getUsers(page, limit, filters),
    });
};

export const getUser = async (userId: string) => {
    return callApi<UserDetail>({
        url: `${usersApiUrl}/${userId}`,
        method: 'GET',
    });
};

export const useUser = (userId: string, enabled = true) => {
    return useQuery({
        queryKey: ['user', userId],
        queryFn: () => getUser(userId),
        enabled: !!userId && enabled,
    });
};

/**
 * `course33Access` is a write-only admin toggle (not a persisted User field) —
 * the API translates it to `course33AccessExpiresAt` (grant the 1-year window,
 * or revoke).
 *
 * The API enforces an explicit field whitelist, so anything not on it is
 * rejected rather than silently ignored: the auto-written fields
 * (`telegramGroupJoinedAt`, `telegramGroupInviteSentAt`,
 * `redeemedPromotionCode`, `latestOrderStatus`) must never be sent from here.
 */
export const updateUser = async (userId: string, userData: Partial<User> & { course33Access?: boolean }) => {
    return callApi({
        url: `${usersApiUrl}/${userId}`,
        method: 'PUT',
        data: userData,
    });
};

export const deleteUser = async (userId: string) => {
    return callApi({
        url: `${usersApiUrl}/${userId}`,
        method: 'DELETE',
    });
};
